import { createApp } from "vue";
import { createPinia } from "pinia";
import Antd from "ant-design-vue";
import App from "./App.vue";
import router from "./router";
import { jwtService } from "@/services/jwt";
import axios from "axios";
import { i18n } from "./i18n";
import { notificationService } from "@/services/notificationService";
import { useSystemStatusStore } from "./stores/systemStatus";
import {
  initializeSentry,
  setCurrentInstances,
  updateSentryUser,
  clearSentryUser,
} from "@/utils/sentry";
import { systemConfigService } from "@/services/systemConfigService";
import {
  initializeSystemRoutes,
  setupSystemConfigWatcher,
} from "@/router/systems";
import { useAuthStore } from "@/stores/auth";

import "./assets/styles/main.css";
import "./assets/styles/global.less";
import "ant-design-vue/dist/reset.css";
import "vue-sonner/style.css";

// PageSpy 初始化
const initializePageSpy = () => {
  // 检查 PageSpy 是否启用
  const isPageSpyEnabled = import.meta.env.VITE_ENABLE_PAGESPY === "true";

  if (!isPageSpyEnabled) {
    console.log("PageSpy 调试工具已禁用 (VITE_ENABLE_PAGESPY=false)");
    return;
  }

  // PageSpy 类型声明
  interface WindowWithPageSpy extends Window {
    PageSpy?: {
      new (config: unknown): unknown;
      registerPlugin: (plugin: unknown) => void;
    };
    DataHarborPlugin?: new () => unknown;
    RRWebPlugin?: new () => unknown;
    $pageSpy?: unknown;
  }

  // 等待 PageSpy 脚本加载完成
  const checkPageSpyReady = () => {
    const windowWithPageSpy = window as WindowWithPageSpy;
    if (
      typeof windowWithPageSpy.PageSpy !== "undefined" &&
      typeof windowWithPageSpy.DataHarborPlugin !== "undefined" &&
      typeof windowWithPageSpy.RRWebPlugin !== "undefined"
    ) {
      try {
        const harbor = new windowWithPageSpy.DataHarborPlugin!();
        const rrweb = new windowWithPageSpy.RRWebPlugin!();
        windowWithPageSpy.PageSpy!.registerPlugin(harbor);
        windowWithPageSpy.PageSpy!.registerPlugin(rrweb);
        windowWithPageSpy.$pageSpy = new windowWithPageSpy.PageSpy!({
          serializeData: true,
          project: "vue-abdnsso",
          title: "阿伯丁SSO系统",
        });

        console.log("PageSpy 初始化成功");
      } catch (error) {
        console.error("PageSpy 初始化失败:", error);
      }
    } else {
      // 如果脚本还没加载完成，500ms后重试
      setTimeout(checkPageSpyReady, 500);
    }
  };

  // 开始检查和初始化
  checkPageSpyReady();
};

const app = createApp(App);
const pinia = createPinia();
app.use(pinia);

// 初始化 Sentry
if (import.meta.env.PROD) {
  // 使用新的 Sentry 工具初始化
  initializeSentry(app, router);
  setCurrentInstances(app, router);
}

// 监听 token 变化
const originalSetTokens = jwtService.setTokens.bind(jwtService);
jwtService.setTokens = async function (
  accessOrTokens: string | import("@/services/jwt").TokenResponse,
  refresh?: string,
): Promise<void> {
  if (typeof accessOrTokens === "string" && refresh) {
    await originalSetTokens(accessOrTokens, refresh);
  } else if (typeof accessOrTokens === "object") {
    await originalSetTokens(accessOrTokens);
  } else {
    throw new Error("Invalid arguments for setTokens");
  }
  // 更新 Sentry 用户信息
  updateSentryUser();
};

const originalClearTokens = jwtService.clearTokens;
jwtService.clearTokens = async function () {
  originalClearTokens.call(this);
  // 清除 Sentry 用户信息
  clearSentryUser();
};

// 添加全局错误处理
app.config.errorHandler = async (err: unknown) => {
  if (axios.isAxiosError(err) && err.response?.status === 401) {
    // 密钥更换期间，所有401直接清除token并跳转登录页
    await jwtService.clearTokens();
    router.push("/login");
    return;
  }
  console.error(err);
  // 错误已经由 Sentry 初始化时配置的处理器捕获
};

// 初始化系统配置和路由
// 确保在应用启动前配置服务已初始化
Promise.resolve().then(async () => {
  // 等待配置加载完成
  await new Promise((resolve) => {
    const checkConfig = () => {
      if (Object.keys(systemConfigService.systems).length > 0) {
        resolve(true);
      } else {
        setTimeout(checkConfig, 100);
      }
    };
    checkConfig();
  });

  // 初始化系统路由
  initializeSystemRoutes();

  // 设置配置变更监听
  setupSystemConfigWatcher();

  console.log("系统配置和路由初始化完成");
});

// 初始化系统状态管理器
const systemStatusStore = useSystemStatusStore();
systemStatusStore.refreshStatus().catch(console.error);

// 初始化认证状态
const authStore = useAuthStore();
// 如果存在有效的 JWT token，初始化用户状态
const token = jwtService.getToken();
if (token) {
  authStore.fetchUserFromToken().catch((error) => {
    console.error("Failed to initialize auth state:", error);
    // 如果初始化失败，清理token状态
    jwtService.clearTokens();
  });
}

// 定期刷新系统状态
setInterval(() => {
  systemStatusStore.refreshStatus().catch(console.error);
}, 60 * 1000); // 每1分钟刷新一次

// 预缓存关键资源已禁用
/* const criticalResources = [
  "/",
  "/login",
  "/authorize",
  "/manifest.json",
  "/favicon.ico",
];

cacheService.precacheResources(criticalResources).catch(console.error); */

// 恢复路由状态（如果存在）
const restorePath = sessionStorage.getItem("_restore_path");
if (restorePath) {
  sessionStorage.removeItem("_restore_path");
  router.replace(restorePath).catch(console.error);
}

app.use(router);
app.use(Antd);
app.use(i18n);

// 初始化通知服务
notificationService.init(i18n as any);

// 在应用挂载后初始化 PageSpy
app.mount("#app");

// DOM 加载完成后初始化 PageSpy
document.addEventListener("DOMContentLoaded", () => {
  initializePageSpy();
});

// 如果 DOM 已经加载完成，立即初始化
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializePageSpy);
} else {
  initializePageSpy();
}
